import { IconVideoCameraOffLine } from '@smarterservices/smarter-ui';
import { useEffect, useState } from 'react';
import { useAppStore } from '../../../stores/app';
import PageInfo from '../../PageInfo';

/**
 * ParticipantConnected Component
 * 
 * Displays platform-specific instructions when a second camera participant is connected.
 * The component automatically detects the participant's device type (iOS, Android, or other)
 * and shows appropriate instructions for that platform.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.stepData - Step configuration data containing instructions
 * @param {Object} props.stepData.participantConnectedInstructions - Platform-specific instruction texts
 * @param {string} props.stepData.participantConnectedInstructions.ios - Instructions for iOS devices
 * @param {string} props.stepData.participantConnectedInstructions.android - Instructions for Android devices
 * @param {string} props.stepData.participantConnectedInstructions.other - Instructions for other platforms
 * @param {Object} props.replacements - Text replacement tokens for dynamic content
 * @returns {React.ReactElement} PageInfo component with platform-specific instructions
 */
const ParticipantConnected = ({ stepData, replacements }) => {
    // Get second camera participant data from the app store
    const secondCameraParticipantMetaData = useAppStore((state) => state.secondCameraParticipant?.metadata);
    const secondCameraParticipant = useAppStore((state) => state.secondCameraParticipant);

    // Local state to hold the platform-specific instructions
    const [instructions, setInstructions] = useState('');

    /**
     * Effect to update instructions based on participant's device type
     * Runs when participant connection state or metadata changes
     */
    useEffect(() => {
        // Exit early if no participant is connected
        if (!secondCameraParticipant) {
            return;
        }
        
        // Get device data to determine platform type
        const deviceData = useAppStore.getState().getSecondCameraParticipantDeviceData();
        
        // Set platform-specific instructions based on device type
        if (deviceData.isIOS) {
            setInstructions(stepData?.participantConnectedInstructions?.ios);
        } else if (deviceData.isAndroid) {
            setInstructions(stepData?.participantConnectedInstructions?.android);
        } else {
            setInstructions(stepData?.participantConnectedInstructions?.other);
        }
    }, [secondCameraParticipant, secondCameraParticipantMetaData, stepData]);


    // Render the PageInfo component with platform-specific instructions
    return (
        <PageInfo
            content={instructions}
            replacements={replacements}
            icon={<IconVideoCameraOffLine color="brand" size="large" />}
        />
    );
};

export default ParticipantConnected;
